import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAdminApi } from "../hooks/use-admin-api";
import { useWedding } from "../contexts/wedding-context";
import { cn } from "../lib/utils";
import {
  Loader2, Check, X, ChevronLeft, ChevronRight, Play,
  RotateCcw, RefreshCw, CheckCheck, Images, Heart, MessageCircle, Trash2, Undo2, Lock } from "lucide-react";
import { MEDIA_DELETE_GRACE_DAYS } from "@wedding/shared";

const PENDING_LIMIT = 20;
const REVIEWED_LIMIT = 20;
const DELETED_LIMIT = 20;
const AUTO_REFRESH_SECS = 60;

interface PendingMedia {
  id: string;
  guest_id: string;
  kind: "image" | "video";
  r2_key: string;
  content_type: string;
  bytes: number;
  created_at: string;
  guest_phone: string;
  guest_name: string | null;
  /** 1 → yükleyen misafir silinmiş; ad yine gösterilir, rozetle işaretlenir. */
  guest_deleted?: number | null;
}

interface ReviewedMedia extends PendingMedia {
  status: "approved" | "rejected";
  reviewed_by: string | null;
  reviewed_at: string | null;
  deleted_at?: string | null;
}

/** Silinmiş ama grace penceresi içindeki medya — geri alınabilir. */
interface DeletedMedia extends PendingMedia {
  status: "approved" | "pending";
  deleted_at: string;
  /** 054: `guest` = misafir kendi sildi → durum kilitli, geri alınamaz (salt okunur). */
  deleted_by?: "guest" | "organizer" | "admin" | "system" | null;
}

/** Kalıcı silinmeye kalan süre — organizatör paneliyle aynı metinler. */
function formatDeletionTime(deletedAt: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const remainingMs = new Date(deletedAt).getTime() + MEDIA_DELETE_GRACE_DAYS * 86_400_000 - Date.now();
  if (remainingMs <= 0) return t("moderation.deletesIn", { days: 0 });
  const hours = Math.ceil(remainingMs / 3_600_000);
  if (hours <= 1) return t("moderation.deletesImminently");
  if (hours <= 24) return t("moderation.deletesInHours", { hours });
  return t("moderation.deletesIn", { days: Math.ceil(remainingMs / 86_400_000) });
}

// ─── Moderation Lightbox ───────────────────────────────────────────────────
interface OrgComment {
  id: string;
  content: string;
  author_name: string | null;
  guest_id: string;
  created_at: string;
}

function ModerationLightbox({
  items, index, tab, slug, onClose, onNavigate, onAction, actionPending,
}: {
  items: (PendingMedia | ReviewedMedia)[];
  index: number;
  tab: "pending" | "approved" | "rejected";
  slug: string;
  onClose: () => void;
  onNavigate: (i: number) => void;
  onAction: (id: string, action: "approve" | "reject" | "pending") => void;
  actionPending: boolean;
}) {
  const { t, i18n } = useTranslation();
  const api = useAdminApi();
  const item = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  const [comments, setComments] = useState<OrgComment[]>([]);
  const [likeCount, setLikeCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const commentsEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!item?.id) return;
    let cancelled = false;
    setCommentsLoading(true);
    setComments([]);
    setLikeCount(0);
    setCommentCount(0);
    api.get<OrgComment[]>(`/media/${item.id}/comments`).then(res => {
      if (cancelled) return;
      const r = res as typeof res & { like_count?: number; comment_count?: number };
      setComments((r.data ?? []) as OrgComment[]);
      setLikeCount(r.like_count ?? 0);
      setCommentCount(r.comment_count ?? 0);
    }).catch(() => {}).finally(() => { if (!cancelled) setCommentsLoading(false); });
    return () => { cancelled = true; };
  }, [item?.id, api]);

  useEffect(() => {
    if (comments.length > 0) commentsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [comments.length]);

  const handleDeleteComment = useCallback(async (commentId: string) => {
    try {
      await api.delete(`/media/${item.id}/comments/${commentId}`);
      setComments(c => c.filter(x => x.id !== commentId));
      setCommentCount(n => Math.max(0, n - 1));
    } catch { /* silent */ }
  }, [api, item?.id]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowLeft" && hasPrev) { onNavigate(index - 1); return; }
      if (e.key === "ArrowRight" && hasNext) { onNavigate(index + 1); return; }
      if (!actionPending && item) {
        if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
        if (e.key === "a" || e.key === "A") onAction(item.id, "approve");
        if (e.key === "r" || e.key === "R") onAction(item.id, "reject");
        if (e.key === "p" || e.key === "P") onAction(item.id, "pending");
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, hasPrev, hasNext, onClose, onNavigate, tab, actionPending, item, onAction]);

  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  }, []);
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStart) return;
    const dx = e.changedTouches[0].clientX - touchStart.x;
    const dy = e.changedTouches[0].clientY - touchStart.y;
    if (Math.abs(dy) > 80 && Math.abs(dy) > Math.abs(dx) && dy > 0) { onClose(); return; }
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0 && hasNext) onNavigate(index + 1);
      if (dx > 0 && hasPrev) onNavigate(index - 1);
    }
    setTouchStart(null);
  }, [touchStart, index, hasPrev, hasNext, onClose, onNavigate]);

  const formattedDate = new Date(item.created_at).toLocaleString(i18n.language, {
    month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });

  const reviewedItem = "reviewed_by" in item ? (item as ReviewedMedia) : null;

  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/92 backdrop-blur-sm flex items-center justify-center p-4 lg:p-12">
      <button
        onClick={onClose}
        className="fixed top-4 right-4 z-[10000] size-10 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 flex items-center justify-center text-white transition-colors cursor-pointer"
      >
        <X className="h-5 w-5" />
      </button>

      <div
        className="flex flex-col lg:flex-row w-full h-full max-w-7xl rounded-2xl overflow-hidden shadow-2xl"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {/* ── Left: Media (70%) ── */}
        <div className="relative flex flex-1 lg:w-[70%] bg-neutral-950 items-center justify-center overflow-hidden">
          {hasPrev && (
            <button
              onClick={e => { e.stopPropagation(); onNavigate(index - 1); }}
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 hidden md:flex size-11 rounded-full bg-black/40 border border-white/10 items-center justify-center hover:bg-black/70 text-white transition-colors cursor-pointer"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
          )}
          {hasNext && (
            <button
              onClick={e => { e.stopPropagation(); onNavigate(index + 1); }}
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 hidden md:flex size-11 rounded-full bg-black/40 border border-white/10 items-center justify-center hover:bg-black/70 text-white transition-colors cursor-pointer"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          )}
          <div className="relative w-full h-full flex items-center justify-center">
            {item.kind === "image" ? (
              <img key={item.id} src={`/api/admin/w/${slug}/media/${item.id}/raw`} alt="" className="max-h-full max-w-full object-contain select-none" draggable={false} />
            ) : (
              <video key={item.id} src={`/api/admin/w/${slug}/media/${item.id}/raw`} className="max-h-full max-w-full object-contain" controls autoPlay playsInline />
            )}
          </div>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-xs font-medium text-white/60 bg-black/40 px-3 py-1 rounded-full pointer-events-none">
            {index + 1} / {items.length}
          </div>
        </div>

        {/* ── Right: Social + Actions (30%) ── */}
        <div className="flex flex-col lg:w-[30%] lg:min-w-[340px] border-t border-border lg:border-t-0 lg:border-l border-border max-h-[45vh] lg:max-h-full bg-card">
          <div className="flex-shrink-0 flex items-center gap-3 px-5 py-4 border-b border-border">
            <div className="size-10 rounded-full bg-primary/20 border border-border flex items-center justify-center text-sm font-bold text-foreground shrink-0 select-none">
              {item.guest_name?.[0]?.toUpperCase() ?? "?"}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-foreground truncate">
                {item.guest_name ?? item.guest_phone}{item.guest_deleted === 1 && ` · ${t("moderation.guestDeleted")}`}
              </p>
              <p className="text-xs text-muted-foreground">{formattedDate}</p>
            </div>
            {(likeCount > 0 || commentCount > 0) && (
              <div className="flex items-center gap-3 shrink-0">
                {likeCount > 0 && (
                  <span className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Heart className="h-4 w-4" /> {likeCount}
                  </span>
                )}
                {commentCount > 0 && (
                  <span className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MessageCircle className="h-4 w-4" /> {commentCount}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
            {commentsLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/50" />
              </div>
            ) : comments.length === 0 ? (
              <p className="text-center text-sm text-muted-foreground/60 py-6">{t("gallery.noComments")}</p>
            ) : (
              comments.map(comment => (
                <div key={comment.id} className="flex gap-3 group">
                  <div className="size-8 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-muted-foreground shrink-0 select-none">
                    {comment.author_name?.[0]?.toUpperCase() ?? "?"}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground leading-snug">{comment.author_name}</p>
                    <p className="text-sm text-muted-foreground break-words leading-snug mt-0.5">{comment.content}</p>
                    <p className="text-xs text-muted-foreground/50 mt-1">
                      {new Date(comment.created_at).toLocaleString(i18n.language, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteComment(comment.id)}
                    className="shrink-0 text-muted-foreground/30 hover:text-destructive transition-colors opacity-0 group-hover:opacity-100 mt-0.5 cursor-pointer"
                    title={t("gallery.deleteComment")}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))
            )}
            <div ref={commentsEndRef} />
          </div>

          <div className="flex-shrink-0 border-t border-border px-4 py-4 flex flex-col gap-2">
            {tab === "pending" && (
              <>
                <button onClick={() => onAction(item.id, "approve")} disabled={actionPending}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 cursor-pointer">
                  <Check className="h-4 w-4" /> {t("moderation.approve")} <span className="text-xs opacity-50 ml-1">A</span>
                </button>
                <button onClick={() => onAction(item.id, "reject")} disabled={actionPending}
                  className="w-full flex items-center justify-center gap-2 bg-muted text-foreground border border-border py-3 rounded-xl text-sm font-bold hover:bg-muted/80 transition-colors disabled:opacity-50 cursor-pointer">
                  <X className="h-4 w-4" /> {t("moderation.reject")} <span className="text-xs opacity-40 ml-1">R</span>
                </button>
              </>
            )}
            {tab === "rejected" && (
              <>
                <button onClick={() => onAction(item.id, "approve")} disabled={actionPending}
                  className="w-full flex items-center justify-center gap-2 bg-primary text-primary-foreground py-3 rounded-xl text-sm font-bold hover:bg-primary/90 transition-colors disabled:opacity-50 cursor-pointer">
                  <Check className="h-4 w-4" /> {t("moderation.approve")} <span className="text-xs opacity-50 ml-1">A</span>
                </button>
                <button onClick={() => onAction(item.id, "pending")} disabled={actionPending}
                  className="w-full flex items-center justify-center gap-2 bg-primary/15 text-primary border border-primary/25 py-3 rounded-xl text-sm font-bold hover:bg-primary/25 transition-colors disabled:opacity-50 cursor-pointer">
                  <RotateCcw className="h-4 w-4" /> {t("moderation.returnToPending")} <span className="text-xs opacity-50 ml-1">P</span>
                </button>
              </>
            )}
            {tab === "approved" && (
              <>
                <button onClick={() => onAction(item.id, "reject")} disabled={actionPending}
                  className="w-full flex items-center justify-center gap-2 bg-muted text-foreground border border-border py-3 rounded-xl text-sm font-bold hover:bg-muted/80 transition-colors disabled:opacity-50 cursor-pointer">
                  <X className="h-4 w-4" /> {t("moderation.reject")} <span className="text-xs opacity-40 ml-1">R</span>
                </button>
                <button onClick={() => onAction(item.id, "pending")} disabled={actionPending}
                  className="w-full flex items-center justify-center gap-2 bg-primary/15 text-primary border border-primary/25 py-3 rounded-xl text-sm font-bold hover:bg-primary/25 transition-colors disabled:opacity-50 cursor-pointer">
                  <RotateCcw className="h-4 w-4" /> {t("moderation.returnToPending")} <span className="text-xs opacity-50 ml-1">P</span>
                </button>
              </>
            )}
            {reviewedItem?.reviewed_by && (
              <p className="text-center text-xs text-muted-foreground/40 pt-1">
                {t("moderation.reviewedBy", { email: reviewedItem.reviewed_by })}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── Skeleton cards ────────────────────────────────────────────────────────
const SKELETON_HEIGHTS = ["h-52", "h-36", "h-64", "h-44", "h-72", "h-40", "h-56", "h-48", "h-60", "h-32", "h-68", "h-44"];
function MasonrySkeleton() {
  return (
    <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
      {SKELETON_HEIGHTS.map((h, i) => (
        <div key={i} className={cn("break-inside-avoid mb-4 rounded-2xl bg-muted animate-pulse", h)} />
      ))}
    </div>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────
export function ModerationPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const api = useAdminApi();
  const { slug } = useWedding();

  const [tab, setTab] = useState<"pending" | "approved" | "rejected" | "deleted">("pending");
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_REFRESH_SECS);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [isBulking, setIsBulking] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 2500);
  }, []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const navigateLightbox = useCallback((i: number) => setLightboxIndex(i), []);

  const [items, setItems] = useState<PendingMedia[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pendingLoadingMore, setPendingLoadingMore] = useState(false);
  const [pendingHasMore, setPendingHasMore] = useState(false);

  const fetchPending = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await api.get<PendingMedia[]>(`/media/pending?limit=${PENDING_LIMIT}`);
      const rows = res.data ?? [];
      setItems(rows);
      setPendingHasMore(rows.length >= PENDING_LIMIT);
      setSelected(new Set());
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      setIsLoading(false);
    }
  }, [api, showToast, t]);

  const fetchMorePendingRef = useRef(false);
  const fetchMorePending = useCallback(async () => {
    if (fetchMorePendingRef.current || !items.length) return;
    fetchMorePendingRef.current = true;
    setPendingLoadingMore(true);
    try {
      const cursor = items[items.length - 1].created_at;
      const res = await api.get<PendingMedia[]>(`/media/pending?limit=${PENDING_LIMIT}&cursor=${encodeURIComponent(cursor)}`);
      const rows = res.data ?? [];
      setItems(prev => [...prev, ...rows]);
      setPendingHasMore(rows.length >= PENDING_LIMIT);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      fetchMorePendingRef.current = false;
      setPendingLoadingMore(false);
    }
  }, [api, items, showToast, t]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  useEffect(() => {
    if (!autoRefresh) { setCountdown(AUTO_REFRESH_SECS); return; }
    setCountdown(AUTO_REFRESH_SECS);
    const fetchId = setInterval(fetchPending, AUTO_REFRESH_SECS * 1000);
    const tickId  = setInterval(() => setCountdown(c => c <= 1 ? AUTO_REFRESH_SECS : c - 1), 1000);
    return () => { clearInterval(fetchId); clearInterval(tickId); };
  }, [autoRefresh, fetchPending]);

  const [reviewedItems, setReviewedItems] = useState<ReviewedMedia[]>([]);
  const [reviewedLoading, setReviewedLoading] = useState(false);
  const [reviewedLoadingMore, setReviewedLoadingMore] = useState(false);
  const [reviewedHasMore, setReviewedHasMore] = useState(false);

  const fetchReviewed = useCallback(async () => {
    setReviewedLoading(true);
    try {
      const res = await api.get<ReviewedMedia[]>(`/media/reviewed?limit=${REVIEWED_LIMIT}`);
      const rows = res.data ?? [];
      setReviewedItems(rows);
      setReviewedHasMore(rows.length >= REVIEWED_LIMIT);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      setReviewedLoading(false);
    }
  }, [api, showToast, t]);

  const fetchMoreReviewedRef = useRef(false);
  const fetchMoreReviewed = useCallback(async () => {
    if (fetchMoreReviewedRef.current || !reviewedItems.length) return;
    fetchMoreReviewedRef.current = true;
    setReviewedLoadingMore(true);
    try {
      const cursor = reviewedItems[reviewedItems.length - 1].created_at;
      const res = await api.get<ReviewedMedia[]>(`/media/reviewed?limit=${REVIEWED_LIMIT}&cursor=${encodeURIComponent(cursor)}`);
      const rows = res.data ?? [];
      setReviewedItems(prev => [...prev, ...rows]);
      setReviewedHasMore(rows.length >= REVIEWED_LIMIT);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      fetchMoreReviewedRef.current = false;
      setReviewedLoadingMore(false);
    }
  }, [api, reviewedItems, showToast, t]);

  const hasFetchedReviewedRef = useRef(false);
  useEffect(() => {
    if ((tab === "approved" || tab === "rejected") && !hasFetchedReviewedRef.current) {
      hasFetchedReviewedRef.current = true;
      fetchReviewed();
    }
  }, [tab, fetchReviewed]);

  // ─── Silinen medya (kurtarma kuyruğu) ─────────────────────
  const [deletedItems, setDeletedItems] = useState<DeletedMedia[]>([]);
  const [deletedLoading, setDeletedLoading] = useState(false);

  const fetchDeleted = useCallback(async () => {
    setDeletedLoading(true);
    try {
      const res = await api.get<DeletedMedia[]>(`/media/deleted?limit=${DELETED_LIMIT}`);
      setDeletedItems(res.data ?? []);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    } finally {
      setDeletedLoading(false);
    }
  }, [api, showToast, t]);

  useEffect(() => {
    if (tab === "deleted") fetchDeleted();
  }, [tab, fetchDeleted]);

  const restoreMutation = useMutation({
    mutationFn: (id: string) => api.post(`/media/${id}/restore`, {}),
    onSuccess: () => {
      fetchDeleted();
      if (reviewedItems.length > 0) fetchReviewed();
      queryClient.invalidateQueries({ queryKey: ["admin", slug, "stats"] });
      showToast(t("moderation.restored"));
    },
    onError: (err: unknown) => {
      showToast(err instanceof Error ? err.message : t("common.error"), false);
    },
  });

  const actionMutation = useMutation({
    mutationFn: ({ id, action }: { id: string; action: "approve" | "reject" | "pending" }) =>
      api.patch(`/media/${id}`, { action }),
    onSuccess: (_, { action }) => {
      fetchPending();
      if (reviewedItems.length > 0) fetchReviewed();
      queryClient.invalidateQueries({ queryKey: ["admin", slug, "stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin", slug, "activity"] });
      setLightboxIndex(null);
      if (action === "approve") showToast(t("moderation.approve") + " ✓");
      else if (action === "reject") showToast(t("moderation.reject") + " ✓", false);
      else showToast(t("moderation.returnToPending") + " ✓");
    },
  });

  const toggleSelect = useCallback((id: string) => {
    setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }, []);

  const reviewedItemsLengthRef = useRef(0);
  reviewedItemsLengthRef.current = reviewedItems.length;

  const handleBulk = useCallback(async (action: "approve" | "reject") => {
    if (selected.size === 0) return;
    setIsBulking(true);
    try {
      await Promise.allSettled([...selected].map(id => api.patch(`/media/${id}`, { action })));
      setSelected(new Set());
      await fetchPending();
      if (reviewedItemsLengthRef.current > 0) fetchReviewed();
      queryClient.invalidateQueries({ queryKey: ["admin", slug, "stats"] });
      queryClient.invalidateQueries({ queryKey: ["admin", slug, "activity"] });
      showToast(action === "approve" ? t("moderation.bulkApproved") : t("moderation.bulkRejected"));
    } catch {
      showToast(t("common.error"), false);
    } finally {
      setIsBulking(false);
    }
  }, [selected, api, fetchPending, fetchReviewed, queryClient, slug, showToast, t]);

  const itemsWithDate = useMemo(
    () => items.map(item => ({ ...item, _formattedDate: new Date(item.created_at).toLocaleString(i18n.language) })),
    [items, i18n.language]
  );
  const reviewedItemsWithDate = useMemo(
    () => reviewedItems.map(item => ({ ...item, _formattedDate: new Date(item.created_at).toLocaleString(i18n.language) })),
    [reviewedItems, i18n.language]
  );

  const filteredReviewed = useMemo(
    () => reviewedItemsWithDate.filter(i => i.status === tab),
    [reviewedItemsWithDate, tab]
  );

  // "Silinenler" sekmesinde lightbox yok — tek aksiyon geri alma.
  const lightboxItems = useMemo((): (PendingMedia | ReviewedMedia)[] => {
    if (tab === "pending") return items;
    if (tab === "deleted") return [];
    return reviewedItems.filter(i => i.status === tab);
  }, [tab, items, reviewedItems]);

  const bulkBarVisible = selected.size > 0 && tab === "pending";

  const approveBtn = "flex items-center justify-center gap-1 flex-1 bg-primary text-white hover:bg-primary/90 py-2 rounded-xl text-xs font-bold transition-colors disabled:opacity-50 cursor-pointer";
  const rejectBtn  = "flex items-center justify-center gap-1 flex-1 bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground py-2 rounded-xl text-xs font-bold transition-colors disabled:opacity-50 cursor-pointer border border-border";
  const pendingBtn = "flex items-center justify-center gap-1 flex-1 py-2 rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50 cursor-pointer text-xs font-bold";

  return (
    <div className="space-y-6 pb-28">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="font-serif italic text-3xl text-foreground leading-tight">
            {t("moderation.title")}
          </h2>
          {items.length > 0 && (
            <p className="mt-1 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 px-2.5 py-0.5 text-xs font-semibold">
                {items.length} {t("moderation.pending")}
              </span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={fetchPending}
            disabled={isLoading}
            className="inline-flex items-center gap-1.5 rounded-xl border border-border px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer disabled:opacity-50"
          >
            <RefreshCw className={cn("h-3.5 w-3.5", isLoading && "animate-spin")} />
            {t("moderation.refreshNow")}
          </button>
          <button
            onClick={() => setAutoRefresh(v => !v)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium transition-colors cursor-pointer",
              autoRefresh
                ? "bg-primary/10 border-primary/30 text-primary"
                : "border-border text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
          >
            <RefreshCw className={cn("h-3.5 w-3.5", autoRefresh && "animate-spin [animation-duration:4s]")} />
            {autoRefresh ? t("moderation.autoRefreshOn", { s: countdown }) : t("moderation.autoRefreshOff")}
          </button>
        </div>
      </div>

      {/* ── Main tabs ── */}
      <div className="flex items-center gap-1 border-b border-border">
        {(["pending", "approved", "rejected"] as const).map(t_ => (
          <button
            key={t_}
            onClick={() => { setTab(t_); setLightboxIndex(null); }}
            className={cn(
              "relative px-4 py-2.5 text-sm font-semibold transition-colors cursor-pointer",
              tab === t_ ? "text-primary" : "text-muted-foreground hover:text-foreground"
            )}
          >
            {t_ === "pending" ? t("moderation.pending") : t_ === "approved" ? t("moderation.tabApproved") : t("moderation.tabRejected")}
            {t_ === "pending" && items.length > 0 && (
              <span className={cn(
                "ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold",
                tab === "pending" ? "bg-primary text-white" : "bg-muted text-muted-foreground"
              )}>
                {items.length}
              </span>
            )}
            {t_ !== "pending" && reviewedItems.filter(i => i.status === t_).length > 0 && (
              <span className={cn(
                "ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold",
                tab === t_ ? "bg-primary text-white" : "bg-muted text-muted-foreground"
              )}>
                {reviewedItems.filter(i => i.status === t_).length}
              </span>
            )}
            {tab === t_ && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />}
          </button>
        ))}
        {/* Silinenler döngünün dışında: sayacı reviewedItems'tan değil kendi
            listesinden gelir ve ikonu var. */}
        <button
          onClick={() => { setTab("deleted"); setLightboxIndex(null); }}
          className={cn(
            "relative px-4 py-2.5 text-sm font-semibold transition-colors cursor-pointer flex items-center gap-1.5",
            tab === "deleted" ? "text-primary" : "text-muted-foreground hover:text-foreground"
          )}
        >
          <Trash2 className="h-3.5 w-3.5" />
          {t("moderation.tabDeleted")}
          {deletedItems.length > 0 && (
            <span className={cn(
              "inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-bold",
              tab === "deleted" ? "bg-primary text-white" : "bg-muted text-muted-foreground"
            )}>
              {deletedItems.length}
            </span>
          )}
          {tab === "deleted" && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-t-full" />}
        </button>
      </div>

      {/* ── Pending tab ── */}
      {tab === "pending" && (
        isLoading ? <MasonrySkeleton /> :
        items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Images className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
            <p className="text-muted-foreground">{t("moderation.noItems")}</p>
          </div>
        ) : (
          <>
            {selected.size > 0 && (
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{t("moderation.selected", { count: selected.size })}</span>
                <button onClick={() => setSelected(new Set(items.map(i => i.id)))} className="hover:text-foreground transition-colors cursor-pointer">{t("moderation.selectAll")}</button>
                <button onClick={() => setSelected(new Set())} className="hover:text-foreground transition-colors cursor-pointer">{t("moderation.deselectAll")}</button>
              </div>
            )}

            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
              {itemsWithDate.map((item, i) => (
                <div key={item.id} className="break-inside-avoid mb-4 group relative rounded-2xl overflow-hidden border border-border bg-card dark:bg-[rgba(27,24,23,0.75)]">
                  <div className="relative overflow-hidden cursor-pointer" onClick={() => setLightboxIndex(i)}>
                    {item.kind === "image" ? (
                      <img src={`/api/admin/w/${slug}/media/${item.id}/raw`} alt="" className="w-full h-auto block transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                    ) : (
                      <div className="relative">
                        <video src={`/api/admin/w/${slug}/media/${item.id}/raw`} className="w-full h-auto block" muted preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play className="h-10 w-10 text-white/90 drop-shadow" />
                        </div>
                      </div>
                    )}

                    <span className="absolute top-2.5 left-2.5 bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded-full border border-white/20 select-none max-w-[60%] truncate">
                      {item.guest_name ?? item.guest_phone}{item.guest_deleted === 1 && ` · ${t("moderation.guestDeleted")}`}
                    </span>

                    <button
                      className={cn(
                        "absolute top-2.5 right-2.5 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all cursor-pointer z-10",
                        selected.has(item.id)
                          ? "bg-primary border-primary text-white scale-110"
                          : "bg-black/40 border-white/40 backdrop-blur-sm opacity-100 md:opacity-0 md:group-hover:opacity-100"
                      )}
                      onClick={e => { e.stopPropagation(); toggleSelect(item.id); }}
                    >
                      {selected.has(item.id) && <Check className="w-3 h-3" />}
                    </button>

                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                      <div className="hidden md:flex gap-2">
                        <button
                          onClick={e => { e.stopPropagation(); actionMutation.mutate({ id: item.id, action: "approve" }); }}
                          disabled={actionMutation.isPending}
                          className="flex-1 bg-primary/90 text-white py-2 rounded-xl text-xs font-bold hover:bg-primary transition-colors flex items-center justify-center gap-1 disabled:opacity-50 cursor-pointer"
                        >
                          <Check className="w-3.5 h-3.5" /> {t("moderation.approve")}
                        </button>
                        <button
                          onClick={e => { e.stopPropagation(); actionMutation.mutate({ id: item.id, action: "reject" }); }}
                          disabled={actionMutation.isPending}
                          className="flex-1 bg-white/20 text-white py-2 rounded-xl text-xs font-bold hover:bg-white/30 transition-colors flex items-center justify-center gap-1 disabled:opacity-50 cursor-pointer"
                        >
                          <X className="w-3.5 h-3.5" /> {t("moderation.reject")}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-1.5 p-2 md:hidden border-t border-border/40">
                    <button onClick={() => actionMutation.mutate({ id: item.id, action: "approve" })} disabled={actionMutation.isPending} className={approveBtn}>
                      <Check className="w-3.5 h-3.5" /> {t("moderation.approve")}
                    </button>
                    <button onClick={() => actionMutation.mutate({ id: item.id, action: "reject" })} disabled={actionMutation.isPending} className={rejectBtn}>
                      <X className="w-3.5 h-3.5" /> {t("moderation.reject")}
                    </button>
                  </div>

                  <p className="px-3 py-2 text-[11px] text-muted-foreground truncate">
                    {Math.round(item.bytes / 1024)} KB · {item._formattedDate}
                  </p>
                </div>
              ))}
            </div>

            {pendingHasMore && (
              <div className="flex justify-center pt-2">
                <button onClick={fetchMorePending} disabled={pendingLoadingMore} className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-2.5 text-sm font-medium hover:bg-muted transition-colors cursor-pointer disabled:opacity-50">
                  {pendingLoadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t("common.loadMore")}
                </button>
              </div>
            )}
          </>
        )
      )}

      {/* ── Approved / Rejected tabs ── */}
      {(tab === "approved" || tab === "rejected") && (
        <>
          {reviewedLoading ? <MasonrySkeleton /> :
          filteredReviewed.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <Images className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
              <p className="text-muted-foreground">{t("moderation.noItems")}</p>
            </div>
          ) : (
            <>
              <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
                {filteredReviewed.map((item, i) => (
                  <div key={item.id} className="break-inside-avoid mb-4 group relative rounded-2xl overflow-hidden border border-border bg-card dark:bg-[rgba(27,24,23,0.75)]">
                    <div className="relative overflow-hidden cursor-pointer" onClick={() => setLightboxIndex(i)}>
                      {item.kind === "image" ? (
                        <img src={`/api/admin/w/${slug}/media/${item.id}/raw`} alt="" className="w-full h-auto block transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                      ) : (
                        <div className="relative">
                          <video src={`/api/admin/w/${slug}/media/${item.id}/raw`} className="w-full h-auto block" muted preload="metadata" />
                          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                            <Play className="h-10 w-10 text-white/90 drop-shadow" />
                          </div>
                        </div>
                      )}

                      <span className="absolute top-2.5 left-2.5 bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded-full border border-white/20 select-none max-w-[60%] truncate">
                        {item.guest_name ?? item.guest_phone}{item.guest_deleted === 1 && ` · ${t("moderation.guestDeleted")}`}
                      </span>

                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
                        <div className="hidden md:flex gap-2">
                          {item.status === "rejected" && (
                            <button
                              onClick={e => { e.stopPropagation(); actionMutation.mutate({ id: item.id, action: "approve" }); }}
                              disabled={actionMutation.isPending}
                              className="flex-1 bg-primary/90 text-white py-2 rounded-xl text-xs font-bold hover:bg-primary transition-colors flex items-center justify-center gap-1 disabled:opacity-50 cursor-pointer"
                            >
                              <Check className="w-3.5 h-3.5" /> {t("moderation.approve")}
                            </button>
                          )}
                          {item.status === "approved" && (
                            <button
                              onClick={e => { e.stopPropagation(); actionMutation.mutate({ id: item.id, action: "reject" }); }}
                              disabled={actionMutation.isPending}
                              className="flex-1 bg-white/20 text-white py-2 rounded-xl text-xs font-bold hover:bg-white/30 transition-colors flex items-center justify-center gap-1 disabled:opacity-50 cursor-pointer"
                            >
                              <X className="w-3.5 h-3.5" /> {t("moderation.reject")}
                            </button>
                          )}
                          <button
                            onClick={e => { e.stopPropagation(); actionMutation.mutate({ id: item.id, action: "pending" }); }}
                            disabled={actionMutation.isPending}
                            className="px-3 py-2 bg-black/50 text-white/80 rounded-xl text-xs font-bold hover:bg-white/20 transition-colors flex items-center justify-center gap-1 disabled:opacity-50 cursor-pointer"
                          >
                            <RotateCcw className="w-3.5 h-3.5" /> {t("moderation.returnToPending")}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-1.5 p-2 md:hidden border-t border-border/40">
                      {item.status === "rejected" && (
                        <button onClick={() => actionMutation.mutate({ id: item.id, action: "approve" })} disabled={actionMutation.isPending} className={approveBtn}>
                          <Check className="w-3.5 h-3.5" /> {t("moderation.approve")}
                        </button>
                      )}
                      {item.status === "approved" && (
                        <button onClick={() => actionMutation.mutate({ id: item.id, action: "reject" })} disabled={actionMutation.isPending} className={rejectBtn}>
                          <X className="w-3.5 h-3.5" /> {t("moderation.reject")}
                        </button>
                      )}
                      <button onClick={() => actionMutation.mutate({ id: item.id, action: "pending" })} disabled={actionMutation.isPending} className={pendingBtn}>
                        <RotateCcw className="w-3.5 h-3.5" /> {t("moderation.returnToPending")}
                      </button>
                    </div>

                    <div className="px-3 py-2 text-[11px] text-muted-foreground space-y-0.5">
                      <p className="truncate">{item.guest_name ?? item.guest_phone}{item.guest_deleted === 1 && ` · ${t("moderation.guestDeleted")}`} · {Math.round(item.bytes / 1024)} KB</p>
                      {item.reviewed_by && <p className="truncate">{t("moderation.reviewedBy", { email: item.reviewed_by })}</p>}
                    </div>
                  </div>
                ))}
              </div>

              {reviewedHasMore && tab === reviewedItems[reviewedItems.length - 1]?.status && (
                <div className="flex justify-center pt-2">
                  <button onClick={fetchMoreReviewed} disabled={reviewedLoadingMore} className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-2.5 text-sm font-medium hover:bg-muted transition-colors cursor-pointer disabled:opacity-50">
                    {reviewedLoadingMore && <Loader2 className="h-4 w-4 animate-spin" />}
                    {t("common.loadMore")}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ── Silinenler (kurtarma kuyruğu) ── */}
      {tab === "deleted" && (
        deletedLoading ? <MasonrySkeleton /> :
        deletedItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <Trash2 className="mx-auto mb-4 h-12 w-12 text-muted-foreground/30" />
            <p className="text-muted-foreground">{t("moderation.noDeleted")}</p>
          </div>
        ) : (
          <>
            <p className="text-xs text-muted-foreground">
              {t("moderation.deletedHint", { days: MEDIA_DELETE_GRACE_DAYS })}
            </p>
            <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4">
              {deletedItems.map((item) => (
                <div key={item.id} className="break-inside-avoid mb-4 group relative rounded-2xl overflow-hidden border border-border bg-card dark:bg-[rgba(27,24,23,0.75)]">
                  <div className="relative overflow-hidden">
                    {item.kind === "image" ? (
                      <img src={`/api/admin/w/${slug}/media/${item.id}/raw`} alt="" className="w-full h-auto block opacity-70" loading="lazy" />
                    ) : (
                      <div className="relative">
                        <video src={`/api/admin/w/${slug}/media/${item.id}/raw`} className="w-full h-auto block opacity-70" muted preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Play className="h-10 w-10 text-white/90 drop-shadow" />
                        </div>
                      </div>
                    )}

                    <span className="absolute top-2.5 left-2.5 bg-black/60 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-0.5 rounded-full border border-white/20 select-none max-w-[70%] truncate">
                      {item.guest_name ?? item.guest_phone ?? t("moderation.unknownGuest")}
                      {item.guest_deleted === 1 && ` · ${t("moderation.guestDeleted")}`}
                    </span>

                    <span className="absolute top-2.5 right-2.5 bg-rose-600/90 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full border border-rose-400/30 select-none">
                      {formatDeletionTime(item.deleted_at, t)}
                    </span>
                  </div>

                  <div className="p-2 space-y-2">
                    <p className="px-1 text-[11px] text-muted-foreground truncate">
                      {Math.round(item.bytes / 1024)} KB · {new Date(item.created_at).toLocaleString(i18n.language)}
                    </p>
                    {item.deleted_by === "guest" ? (
                      <p data-testid="guest-removed-lock" className="w-full flex items-center justify-center gap-1.5 rounded-xl border border-border py-2 text-xs text-muted-foreground" title={t("moderation.guestRemovedLockedHint")}>
                        <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                        {t("moderation.guestRemovedLocked")}
                      </p>
                    ) : (
                      <button
                        onClick={() => restoreMutation.mutate(item.id)}
                        disabled={restoreMutation.isPending}
                        className="w-full flex items-center justify-center gap-1.5 bg-primary text-white hover:bg-primary/90 py-2 rounded-xl text-xs font-bold transition-colors disabled:opacity-50 cursor-pointer"
                      >
                        {restoreMutation.isPending
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Undo2 className="h-3.5 w-3.5" />}
                        {t("moderation.restore")}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )
      )}

      {/* ── Lightbox ── */}
      {lightboxIndex !== null && lightboxItems.length > 0 && (
        <ModerationLightbox
          items={lightboxItems}
          index={lightboxIndex}
          tab={tab === "deleted" ? "approved" : tab}
          slug={slug}
          onClose={closeLightbox}
          onNavigate={navigateLightbox}
          onAction={(id, action) => actionMutation.mutate({ id, action })}
          actionPending={actionMutation.isPending}
        />
      )}

      {/* ── Bulk action bar ── */}
      {bulkBarVisible && (
        <div className="fixed bottom-28 md:bottom-6 inset-x-0 z-40 flex justify-center px-4 pointer-events-none">
          <div className="pointer-events-auto bg-card/95 backdrop-blur-xl border border-border shadow-2xl rounded-2xl overflow-hidden w-full max-w-md">
            <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-border/50">
              <div className="flex items-center gap-2">
                <span className="bg-primary text-white h-6 w-6 rounded-lg flex items-center justify-center text-xs font-bold shrink-0">
                  {selected.size}
                </span>
                <span className="text-sm font-semibold text-foreground">
                  {t("moderation.selected", { count: selected.size })}
                </span>
              </div>
              <button
                onClick={() => setSelected(new Set())}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors cursor-pointer flex items-center gap-1"
              >
                <X className="h-3 w-3" />
                {t("moderation.deselectAll")}
              </button>
            </div>
            <div className="flex gap-2 p-3">
              <button
                onClick={() => handleBulk("reject")}
                disabled={isBulking}
                className="flex-1 flex items-center justify-center gap-1.5 bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground border border-border px-4 py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 cursor-pointer"
              >
                {isBulking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                {t("moderation.bulkReject")}
              </button>
              <button
                onClick={() => handleBulk("approve")}
                disabled={isBulking}
                className="flex-1 flex items-center justify-center gap-1.5 bg-primary text-white hover:bg-primary/90 px-4 py-2.5 rounded-xl text-sm font-bold transition-colors disabled:opacity-50 cursor-pointer shadow-lg shadow-primary/20"
              >
                {isBulking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCheck className="h-3.5 w-3.5" />}
                {t("moderation.bulkApprove")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={cn(
          "fixed left-1/2 -translate-x-1/2 z-50 rounded-xl px-4 py-2.5 text-sm font-semibold text-white shadow-lg pointer-events-none transition-all",
          bulkBarVisible ? "bottom-60 md:bottom-36" : "bottom-24 md:bottom-6",
          toast.ok ? "bg-emerald-600" : "bg-red-600"
        )}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
